import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { paymentsApi } from "@/lib/payments/payments-api";
import type { DisputeStatus, PaymentSearchParams } from "@/lib/payments/types";

const KEY = ["payments"] as const;

export const usePaymentsList = (params: PaymentSearchParams) =>
	useQuery({
		queryKey: [...KEY, "list", params],
		queryFn: () => paymentsApi.list(params),
		staleTime: 5_000,
	});

export const usePaymentDetail = (id: string | null) =>
	useQuery({
		queryKey: [...KEY, "detail", id],
		queryFn: () => paymentsApi.detail(id as string),
		enabled: Boolean(id),
	});

export const useMomoLive = (enabled = true) =>
	useQuery({
		queryKey: [...KEY, "live"],
		queryFn: paymentsApi.live,
		enabled,
		refetchInterval: 5_000,
	});

export const useForceCheck = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => paymentsApi.forceCheck(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useCreateRefund = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			paymentId,
			...payload
		}: {
			paymentId: string;
			amount: number;
			reasonCode: string;
			reasonText?: string;
			idempotencyKey: string;
		}) => paymentsApi.refunds.create(paymentId, payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useApproveRefund = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (refundId: string) => paymentsApi.refunds.approve(refundId),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useRefundsList = (status?: string) =>
	useQuery({
		queryKey: [...KEY, "refunds", status ?? "all"],
		queryFn: () => paymentsApi.refunds.list(status),
		staleTime: 10_000,
	});

export const useDisputes = (status?: DisputeStatus) =>
	useQuery({
		queryKey: [...KEY, "disputes", status ?? "all"],
		queryFn: () => paymentsApi.disputes.list(status),
		staleTime: 10_000,
	});

export const useProviders = () =>
	useQuery({
		queryKey: [...KEY, "providers"],
		queryFn: paymentsApi.providers.list,
		staleTime: 30_000,
	});

export const useTestProvider = () =>
	useMutation({
		mutationFn: (code: string) => paymentsApi.providers.test(code),
	});

export const useBankStatements = () =>
	useQuery({
		queryKey: [...KEY, "bank-statements"],
		queryFn: paymentsApi.reconciliation.statements,
		staleTime: 30_000,
	});

export const useReconciliationEntries = (statementId?: string) =>
	useQuery({
		queryKey: [...KEY, "reconciliation", statementId ?? "all"],
		queryFn: () => paymentsApi.reconciliation.entries(statementId),
		staleTime: 10_000,
	});

export const useImportStatement = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: {
			source: string;
			periodFrom: string;
			periodTo: string;
			fileContent: string;
			fileName?: string;
		}) => paymentsApi.reconciliation.importStatement(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useClosePeriod = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (statementId: string) =>
			paymentsApi.reconciliation.close(statementId),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useManualMatch = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: {
			entryId: string;
			paymentId: string;
			manualReason: string;
		}) => paymentsApi.reconciliation.match(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useCodEntries = () =>
	useQuery({
		queryKey: [...KEY, "cod"],
		queryFn: paymentsApi.cod.list,
		staleTime: 10_000,
	});
